// The enums of the sales domain that travel in the contracts: @nexxt/db builds the Postgres enums from these tuples.

/**
 * `sales.so_status` (spec v0.2 §5.2): confirmed → [credit_blocked] → allocated → locked → [awaiting_payment] →
 * document_requested → document_issued | document_error → released_to_3pl → completed. `locked` is the allocation
 * frozen, not the credit block: Business Central sees the order from there on.
 */
export const soStatuses = [
  "confirmed",
  "credit_blocked",
  "allocated",
  "locked",
  "awaiting_payment",
  "document_requested",
  "document_error",
  "document_issued",
  "released_to_3pl",
  "completed",
  "cancelled",
] as const;
export type SoStatus = (typeof soStatuses)[number];

/**
 * `sales.fulfillment_issue` (ADR-133): why the process of a locked order (`order-fulfillment`) stopped, for a person to
 * see in a queue and resume it. The status of the order stays where the process left it.
 * - `refused`: a command of the process was refused by the domain that runs it (logistics, documents);
 * - `stalled`: a step kept failing, or what the process waits for did not come in time;
 * - `cancel_failed`: the order was cancelled, and its shipment could not be: it had left.
 */
export const fulfillmentIssues = ["refused", "stalled", "cancel_failed"] as const;
export type FulfillmentIssue = (typeof fulfillmentIssues)[number];

/**
 * `sales.lot_status` (ADR-178 §1.7, ADR-195): where the shipment of a lot, what one lock of an order froze, stands. The
 * process of a lot (`order-fulfillment-v2`) moves it as the one of ADR-133 moved the order: from `locked` through the
 * payment and the documents to the warehouse; `shipped` once the warehouse closed its shipment. The order stays
 * `locked` meanwhile. A lot of a company that invoices after the shipment (ADR-178 §1.9, ADR-197) is released before
 * its invoice, and waits for it once shipped (`awaiting_invoice`): it is `shipped` when its invoice is issued.
 */
export const soLotStatuses = [
  "locked",
  "awaiting_payment",
  "document_requested",
  "document_error",
  "document_issued",
  "released_to_3pl",
  "awaiting_invoice",
  "shipped",
  "cancelled",
] as const;
export type SoLotStatus = (typeof soLotStatuses)[number];

/**
 * `sales.so_channel`: where an order was placed. `claim`: the Customer Service made it from a claim, to replace goods
 * without a price (B-75, ADR-251).
 */
export const soChannels = ["joor", "portal", "manual", "marketplace", "claim"] as const;

/** `sales.so_line_status`. `closed`: nothing more will ship, whatever was shipped; `cancelled`: never to ship. */
export const soLineStatuses = ["open", "allocated", "partially_fulfilled", "fulfilled", "closed", "cancelled"] as const;
export type SoLineStatus = (typeof soLineStatuses)[number];

/** `sales.credit_outcome`: `pending` until Credit Control or Business Central says (I-06). */
export const creditOutcomes = ["pending", "approved", "blocked", "override_approved"] as const;
export type CreditOutcome = (typeof creditOutcomes)[number];

/**
 * `sales.payment_source`: who raised the flag "payment completed" of an order. `manual`: Credit Control; the others the
 * payment tool on Business Central, through I-06 (B-49 point 1, ADR-234).
 */
export const paymentSources = ["manual", "highradius", "business_central"] as const;
export type PaymentSource = (typeof paymentSources)[number];

/**
 * `sales.allocation_run_mode`: who starts a run. `manual`: the Customer Service asks for it (ADR-113); `night` and
 * `day`: a schedule of the warehouse starts it (ADR-186). The night run first takes stock back, as its version of the
 * settings says (`allocationNightReleases`), and commits what it offers by itself (ADR-239, ADR-240); the others offer
 * the stock that is free, and the Customer Service confirms it.
 */
export const allocationRunModes = ["night", "day", "manual"] as const;
export type AllocationRunMode = (typeof allocationRunModes)[number];

/** The modes a schedule starts: a run by hand is asked for, never scheduled. */
export const allocationScheduleModes = ["night", "day"] as const satisfies readonly AllocationRunMode[];
export type AllocationScheduleMode = (typeof allocationScheduleModes)[number];

/**
 * `sales.allocation_ranking_method` (ADR-194): how the rules that sort tell two orders apart. `cascade`: the first
 * rule, in their order, that tells them apart decides, as the engine Nexxt replaces; `weighted`: the lower sum of each
 * rank by the weight of its rule comes first.
 */
export const allocationRankingMethods = ["cascade", "weighted"] as const;
export type AllocationRankingMethod = (typeof allocationRankingMethods)[number];

/**
 * `sales.allocation_night_release` (ADR-239 §1.2, ADR-240): what a night run takes back before it ranks the orders,
 * a setting of each version. `until_lock`: every unit an allocated order not yet locked holds outside its lots, but
 * what a person committed by hand and the orders a run leaves out; `blocked_only`: only the stock of the orders blocked
 * for credit, or of a customer on the blacklist (ADR-193). Those give back everything with either.
 */
export const allocationNightReleases = ["until_lock", "blocked_only"] as const;
export type AllocationNightRelease = (typeof allocationNightReleases)[number];

/** `sales.allocation_run_status`. `queued` until its process ranks the orders; `failed` when the process gave up. */
export const allocationRunStatuses = ["queued", "running", "completed", "failed", "cancelled"] as const;
export type AllocationRunStatus = (typeof allocationRunStatuses)[number];

/**
 * `sales.allocation_status`. `proposed`: what a run offers, which commits no stock until the Customer Service
 * confirms it (ADR-113); `active`: committed; `released`: given back; `fulfilled`: shipped.
 */
export const allocationStatuses = ["proposed", "active", "released", "fulfilled"] as const;

/**
 * `sales.allocation_readiness` (rule 4, spec v0.2 §6): `green` when the value booked for the order reaches the share
 * its size asks for, `yellow` below it, `red` for an order blocked for credit.
 */
export const allocationReadinesses = ["green", "yellow", "red"] as const;
export type AllocationReadiness = (typeof allocationReadinesses)[number];

/**
 * `sales.allocation_block_cause`: why a run offered a line less than it is open for (FR-M7-07).
 * - `credit`: rule 1, the order is blocked for credit and comes after every other: the stock went to the orders
 *   before it, and it takes only what they leave over (ADR-178 §1.2, ADR-193);
 * - `no_availability`: the warehouse has less than the line asks, whatever the ranking;
 * - `outranked`: the stock went to orders ranked before.
 */
export const allocationBlockCauses = ["credit", "no_availability", "outranked"] as const;
export type AllocationBlockCause = (typeof allocationBlockCauses)[number];

/**
 * `sales.allocation_transfer_reason` (ADR-239 §1.2, ADR-240): why a night run took back units another order then
 * takes. `credit`: their order is blocked for credit, or its customer is on the blacklist; `rank`: their order is not
 * yet locked, and an order ranked before it comes first.
 */
export const allocationTransferReasons = ["credit", "rank"] as const;
export type AllocationTransferReason = (typeof allocationTransferReasons)[number];

/**
 * Why a run leaves an order out (spec v0.2 §6), the first that holds in this order. Never stored: it follows from the
 * order as it is.
 * - `blacklisted`: Credit Control put its customer on the blacklist (B-49 point 3, ADR-222), whatever the credit of the
 *   order says, when the version of the settings leaves those orders out (from the F2: ADR-260); in F1 the blacklist
 *   blocks the credit of the order, which takes what is left over;
 * - `no_warehouse`: nobody said which warehouse ships it;
 * - `farfetch`: the customer is one of Farfetch, served from stock kept apart (B-08, ADR-179);
 * - `intercompany`: the customer stands for a company of the group (ADR-179);
 * - `company_not_enabled`: the company that sells does not allocate in Nexxt.
 */
export const allocationExclusions = [
  "blacklisted",
  "no_warehouse",
  "farfetch",
  "intercompany",
  "company_not_enabled",
] as const;
export type AllocationExclusion = (typeof allocationExclusions)[number];

/**
 * Why a confirmation of what a run offered leaves an order as it is (ADR-115), the first that holds. Never stored:
 * the confirmation answers it.
 * - `not_allocable`: the order is no longer confirmed or allocated;
 * - `credit_blocked`: its credit is blocked. No longer given since ADR-193: an order blocked for credit confirms what a
 *   run left over to it; kept for the clients that know it;
 * - `excluded`: a run leaves it out now (`allocationExclusions`);
 * - `no_proposal`: nothing the run offered is left for it, because another run replaced it or the order moved;
 * - `no_stock`: what was offered is no longer there, taken by others or gone from the IMS.
 */
export const allocationSkipReasons = [
  "not_allocable",
  "credit_blocked",
  "excluded",
  "no_proposal",
  "no_stock",
] as const;
export type AllocationSkipReason = (typeof allocationSkipReasons)[number];

/** What the Customer Service does with a change of Joor that waits on an allocated order (B-31, ADR-115). */
export const joorChangeDecisions = ["apply", "dismiss"] as const;
export type JoorChangeDecision = (typeof joorChangeDecisions)[number];

/** `sales.joor_order_status`: what Joor says of an order it had sent as approved (I-14). */
export const joorOrderStatuses = ["approved", "cancelled"] as const;

/**
 * `sales.joor_waiting_reason`: why the latest state of an order of Joor is not on a sales order (ADR-111). The first
 * that holds, in this order; every thing missing is in the issues of the row.
 * - `unknown_brand`, `unknown_customer`: the brand of the envelope, or the retailer, is nobody Nexxt knows;
 * - `missing_legal_entity`: neither the message nor the customer says which company of the group sells;
 * - `missing_mapping`: a code of Joor (season, payment terms) or the currency stands for nothing in Nexxt;
 * - `unknown_item`: a line names no SKU, or more than one;
 * - `totals_mismatch`: the totals of Joor are not the sum of its lines;
 * - `order_in_progress`: the sales order is allocated or beyond, and the change is the Customer Service's to decide
 *   (B-31);
 * - `order_cancelled`: the sales order was cancelled in Nexxt, and Joor still has it approved;
 * - `purchase_reduced`: the state raises a line a PO lowered by its supplier took units off, which would buy them
 *   again: the Customer Service applies or dismisses it (ADR-178 §1.13, ADR-187).
 */
export const joorWaitingReasons = [
  "unknown_brand",
  "unknown_customer",
  "missing_legal_entity",
  "missing_mapping",
  "unknown_item",
  "totals_mismatch",
  "order_in_progress",
  "order_cancelled",
  "purchase_reduced",
] as const;
export type JoorWaitingReason = (typeof joorWaitingReasons)[number];

/**
 * `sales.order_confirmation_status` (ADR-178 §1.8, ADR-198): a confirmation asked for in a run waits for its PDF
 * (`requested`), has it (`issued`), or could not have it (`failed`, with why). Only an issued one counts as a revision
 * the customer received: a failed one leaves its number to the next request.
 */
export const orderConfirmationStatuses = ["requested", "issued", "failed"] as const;
export type OrderConfirmationStatus = (typeof orderConfirmationStatuses)[number];

/**
 * `sales.order_confirmation_run_status`: a run issues the confirmations of a brand and a season, a block at a time;
 * `aborted` when its process gave up, with what it did not issue marked as failed.
 */
export const orderConfirmationRunStatuses = ["running", "completed", "aborted"] as const;
export type OrderConfirmationRunStatus = (typeof orderConfirmationRunStatuses)[number];

/**
 * `sales.order_confirmation_decision` (B-47, ADR-208): what the customer answered to a revision of its order, on
 * `order_confirmation_acceptance`. A rejection has its reason; neither blocks the allocation nor the shipping.
 */
export const orderConfirmationDecisions = ["accepted", "rejected"] as const;
export type OrderConfirmationDecision = (typeof orderConfirmationDecisions)[number];

/**
 * Where the latest revision of an order stands with its customer (ADR-208), a filter of the list of the orders: it
 * waits for an answer (`awaiting`), waits past `sales_order.confirmation_answer_days` (`overdue`, the queue "order
 * confirmations not accepted" of the Customer Service), or was rejected (`rejected`).
 */
export const orderConfirmationAnswers = ["awaiting", "overdue", "rejected"] as const;
export type OrderConfirmationAnswer = (typeof orderConfirmationAnswers)[number];

/**
 * `sales.dunning_step`: what Credit Control did about a customer that does not pay (B-49 point 3, ADR-222). Nexxt
 * records `insurer_escalation` and `cancellation_fee`; the reminders are notifications' (ADR-203), and the blacklist is
 * the customer's, in party: those two values are kept for the rows the model allows.
 */
export const dunningSteps = ["reminder", "insurer_escalation", "cancellation_fee", "blacklist"] as const;
export type DunningStep = (typeof dunningSteps)[number];
